import { directionVectors } from '../game/model/cells';
import type { ArrowDefinition } from '../game/model/types';
import { vacancyFreedPenalty, vacancyLengthPenalty, vacancyReblockWeight } from './curve';
import { nextInt, pickOne, type Rng } from './rng';
import type { VacancyParams } from './types';

export interface VacancyCarvingResult {
  readonly arrows: readonly ArrowDefinition[];
  readonly solutionOrder: readonly number[];
  readonly deletionCount: number;
  readonly scoreCutoffReached: boolean;
}

interface Blocker {
  readonly arrowIndex: number;
  readonly distance: number;
}

function firstBlocker(
  arrows: readonly ArrowDefinition[],
  arrowIndex: number,
  occupancy: Int32Array,
  width: number,
  height: number,
  omittedArrowIndex?: number,
): Blocker | undefined {
  const arrow = arrows[arrowIndex];
  const tip = arrow?.cells.at(-1);
  if (arrow === undefined || tip === undefined) return undefined;
  const step = directionVectors[arrow.direction];
  let distance = 1;
  for (
    let x = tip.x + step.x, y = tip.y + step.y;
    x >= 0 && x < width && y >= 0 && y < height;
    x += step.x, y += step.y, distance += 1
  ) {
    const blockerIndex = (occupancy[y * width + x] ?? 0) - 1;
    if (blockerIndex >= 0 && blockerIndex !== arrowIndex && blockerIndex !== omittedArrowIndex) {
      return { arrowIndex: blockerIndex, distance };
    }
  }
  return undefined;
}

export function carveVacancies(
  arrows: readonly ArrowDefinition[],
  solutionOrder: readonly number[],
  width: number,
  height: number,
  rng: Rng,
  params: VacancyParams,
  protectedArrowIndexes: ReadonlySet<number>,
): VacancyCarvingResult {
  const occupancy = new Int32Array(width * height);
  arrows.forEach((arrow, arrowIndex) => arrow.cells.forEach(({ x, y }) => {
    occupancy[y * width + x] = arrowIndex + 1;
  }));
  const active = new Set(arrows.map((_, arrowIndex) => arrowIndex));
  const targetCount = nextInt(rng, params.deletionCount[0], params.deletionCount[1]);
  let scoreCutoffReached = false;

  while (arrows.length - active.size < targetCount) {
    const candidates = [...active].filter((arrowIndex) => {
      const length = arrows[arrowIndex]?.cells.length ?? 0;
      return length >= 2 && length <= 6 && !protectedArrowIndexes.has(arrowIndex);
    });
    if (candidates.length === 0) break;

    const victimsByBlocker = new Map<number, number[]>();
    for (const arrowIndex of active) {
      const blocker = firstBlocker(arrows, arrowIndex, occupancy, width, height);
      if (blocker === undefined) continue;
      const victims = victimsByBlocker.get(blocker.arrowIndex) ?? [];
      victims.push(arrowIndex);
      victimsByBlocker.set(blocker.arrowIndex, victims);
    }
    const scored = candidates.map((candidateIndex) => {
      let reblockedLong = 0;
      let freed = 0;
      for (const victimIndex of victimsByBlocker.get(candidateIndex) ?? []) {
        const blocker = firstBlocker(
          arrows,
          victimIndex,
          occupancy,
          width,
          height,
          candidateIndex,
        );
        if (blocker === undefined) freed += 1;
        else if (blocker.distance >= params.longRangeDistance) reblockedLong += 1;
      }
      return {
        arrowIndex: candidateIndex,
        score: reblockedLong * vacancyReblockWeight
          - freed * vacancyFreedPenalty
          - (arrows[candidateIndex]?.cells.length ?? 0) * vacancyLengthPenalty,
      };
    });
    const maximumScore = Math.max(...scored.map(({ score }) => score));
    if (maximumScore <= 0) {
      scoreCutoffReached = true;
      break;
    }
    const selected = pickOne(
      rng,
      scored.filter(({ score }) => score === maximumScore),
    ).arrowIndex;
    active.delete(selected);
    arrows[selected]?.cells.forEach(({ x, y }) => {
      occupancy[y * width + x] = 0;
    });
  }

  const oldIndexes = [...active];
  const newIndexByOldIndex = new Map(oldIndexes.map((oldIndex, newIndex) =>
    [oldIndex, newIndex]));
  return {
    arrows: oldIndexes.flatMap((oldIndex) => {
      const arrow = arrows[oldIndex];
      return arrow === undefined ? [] : [arrow];
    }),
    solutionOrder: solutionOrder.flatMap((oldIndex) => {
      const newIndex = newIndexByOldIndex.get(oldIndex);
      return newIndex === undefined ? [] : [newIndex];
    }),
    deletionCount: arrows.length - active.size,
    scoreCutoffReached,
  };
}
